import { useAuth } from "@/features/auth/hooks/useAuth";
import { IS_DEV, IS_PRODUCTION } from "@/features/config";
import { showRepositories, type FeatureFlags } from "@/features/config/featureFlags";
import { useFeatureFlags } from "@/features/config/useFeatureFlags";
import { DATASETS_ROUTE } from "@/features/datasets/routes";

export type NavItem = {
  to: string;
  label: string;
  beta?: boolean;
  /** Extra path prefixes that should also mark this item active. */
  matchPrefixes?: string[];
  /** Local-dev-only destination; shows a developer marker. */
  devOnly?: boolean;
};

export const getNavItems = (flags: FeatureFlags, isAdmin: boolean): NavItem[] => {
  return [
    { to: "/", label: "Home", matchPrefixes: ["/chat"] },
    // Repositories have their own item with the Dataset builder; else a
    // repository page opens under Clipboard.
    {
      to: "/clipboard",
      label: "Clipboard",
      matchPrefixes: showRepositories(flags) ? undefined : ["/repos"],
    },
    // One Datasets item: the dataset builder, or the old curator without datasetBuilder.
    ...(flags.datasetBuilder || flags.dataCuration
      ? [{ to: DATASETS_ROUTE, label: "Datasets" }]
      : []),
    ...(showRepositories(flags) ? [{ to: "/repos", label: "Repositories" }] : []),
    ...(flags.routines ? [{ to: "/workflows", label: "Workflows" }] : []),
    ...(flags.data_usage_tracking ? [{ to: "/usage-tracking", label: "Usage tracking" }] : []),
    { to: "/playground", label: "Playground" },
    ...(!IS_PRODUCTION || flags.evidenceApi
      ? [{ to: "/evidence", label: "Evidence API", matchPrefixes: ["/evidence-api"] }]
      : []),
    ...(flags.paperclipPricing
      ? [{ to: "/pricing", label: "Pricing" }]
      : []),
    { to: "/docs", label: "Docs", matchPrefixes: ["/install"] },
    ...(isAdmin
      ? [{ to: "/admin", label: "Admin" }]
      : []),
    ...(IS_DEV ? [{ to: "/feature-flags", label: "Feature flags", devOnly: true }] : []),
    // API keys and the changelog live under Settings (account menu).
  ];
};

export const useNavItems = () => {
  const { flags } = useFeatureFlags();
  const { user } = useAuth();
  return getNavItems(flags, user?.roles?.includes("admin") ?? false);
};